import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelector from "./index";
import { ResourceSelection, selectionSummary } from "./selection";
import styles from "./styles.module.scss";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
  compareByAdminOrder,
  compareBaysInAdminOrder,
} from "app/utils/request/scheduling/facilityResources";

interface ResourcesBlockProps {
  resources: ScheduleResourceResponse[];
  categories: ResourceCategoryResponse[];
  value: ResourceSelection;
  onChange: (selection: ResourceSelection) => void;
  // The CSS module of the modal, kept for the caller's type but no longer
  // used here: the block now carries its own look (Figma 145:58049,
  // 145:57563, 145:55775) instead of the Payments block's classes, so a fix
  // to this block does not change how the Payments block looks.
  blockStyles: { [className: string]: string };
}

/**
 * The Resources picker of a booking option modal, in a block that opens and
 * closes as the Payments block does. A closed block shows the picked entries
 * on one line (Figma 145:58049). The block starts closed, as Payments does.
 */
const ResourcesBlock: React.FC<ResourcesBlockProps> = ({
  resources,
  categories,
  value,
  onChange,
  blockStyles: _blockStyles,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // The same entries and the same order as the chips of the picker.
  const summary = selectionSummary(
    value,
    resources
      .filter((resource) => resource.kind === "bookable")
      .sort(compareBaysInAdminOrder),
    categories
      .filter((category) => category.kind === "bookable")
      .sort(compareByAdminOrder),
    t("All")
  );

  return (
    <div className={styles.block} data-testid="resources-block">
      <button
        type="button"
        className={styles.header}
        data-testid="resources-toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.header_text}>
          <div className={styles.title}>{t("Resources")}</div>
          {!isOpen && summary.length > 0 && (
            <div className={styles.summary} data-testid="resources-summary">
              {summary.join(", ")}
            </div>
          )}
        </div>
        <i
          className={`ico ico-chevron ${styles.chevron} ${
            isOpen ? styles.open : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className={styles.content}>
          {/* The block header already reads "Resources", so the picker
              shows no label of its own (Figma 145:57563). */}
          <ResourceSelector
            resources={resources}
            categories={categories}
            value={value}
            onChange={onChange}
            showLabel={false}
          />
        </div>
      )}
    </div>
  );
};

export default ResourcesBlock;
